// Photo albums (M60, docs/chat-round-three.md, D219): 2–10 photos or videos
// as one sealed message. The pure parts live here (grouping, layout, the
// envelope, opening an album into its items); uploading and sending are in
// lib/attachments.ts, next to single attachments, so both retry the same way.
import { ALBUM_LIMITS } from '@twinden/shared';
import type { UploadedAttachment } from './api';
import type { FileSecret } from './e2ee/crypto';
import type { AlbumItem, Envelope } from './e2ee/session';
import type { ChatMessage } from '../components/chat/messageUtils';

/**
 * Groups picked files into albums of at most 10, in the order picked. A
 * group of one goes as a plain photo or video (D219).
 */
export function chunkForAlbums<T>(files: T[]): T[][] {
  const groups: T[][] = [];
  for (let i = 0; i < files.length; i += ALBUM_LIMITS.MAX_ITEMS) {
    groups.push(files.slice(i, i + ALBUM_LIMITS.MAX_ITEMS));
  }
  return groups;
}

export type AlbumLayout = {
  /** How the tiles sit: two side by side, one big and two small, or 2×2. */
  kind: 'two' | 'three' | 'grid';
  /** Tiles drawn (the rest are behind "+N" on the last one). */
  shown: number;
  /** "+N" on the last tile, or 0. */
  extra: number;
};

/** The collage for an album of `count` items (D219). */
export function albumLayout(count: number): AlbumLayout {
  if (count <= 2) return { kind: 'two', shown: Math.max(0, count), extra: 0 };
  if (count === 3) return { kind: 'three', shown: 3, extra: 0 };
  return { kind: 'grid', shown: 4, extra: count - 4 };
}

/** One uploaded item, ready to go into the album's envelope. */
export interface UploadedAlbumItem {
  kind: AlbumItem['kind'];
  upload: UploadedAttachment;
  file: FileSecret;
  thumb?: FileSecret;
  mimeType: string;
  fileName: string;
  fileSize: number;
  width?: number;
  height?: number;
  duration?: number;
}

/**
 * The sealed content and the plain file list for an album (§3): each item's
 * file then its preview, in order, with the item pointing at both by index.
 * Only counts go in plain sight.
 */
export function albumEnvelope(
  items: UploadedAlbumItem[],
  caption?: string
): {
  envelope: Envelope;
  files: string[];
  counts: { photos: number; videos: number };
} {
  const files: string[] = [];
  const album: AlbumItem[] = items.map((item) => {
    const file = files.push(item.upload.url) - 1;
    const thumb =
      item.thumb && item.upload.thumbnailUrl
        ? files.push(item.upload.thumbnailUrl) - 1
        : undefined;
    return {
      kind: item.kind,
      file,
      ...(thumb !== undefined ? { thumb, thumbKey: item.thumb } : {}),
      fileKey: item.file,
      mimeType: item.mimeType,
      fileName: item.fileName,
      fileSize: item.fileSize,
      ...(item.width ? { width: item.width } : {}),
      ...(item.height ? { height: item.height } : {}),
      ...(item.duration ? { duration: item.duration } : {}),
    };
  });
  const text = caption?.trim();
  return {
    envelope: { v: 1, ...(text ? { text } : {}), album },
    files,
    counts: {
      photos: album.filter((item) => item.kind !== 'video').length,
      videos: album.filter((item) => item.kind === 'video').length,
    },
  };
}

/**
 * An opened album as one message per item, each shaped like a single
 * photo or video so the existing tiles and viewer can show it. They keep the
 * album's id: starring or replying acts on the album (D219).
 */
export function albumItems(message: ChatMessage): ChatMessage[] {
  const album = message.e2ee?.envelope?.album;
  if (message.type !== 'album' || !album) return [];
  const files = ((message.metadata?.files as string[] | undefined) ??
    []) as string[];
  return album.map((item) => ({
    ...message,
    type: item.kind,
    content: files[item.file] ?? '',
    metadata: {
      fileName: item.fileName,
      fileSize: item.fileSize,
      mimeType: item.mimeType,
      width: item.width,
      height: item.height,
      duration: item.duration,
      ...(item.thumb !== undefined && files[item.thumb]
        ? { thumbnailUrl: files[item.thumb] }
        : {}),
    },
    e2ee: {
      envelope: {
        v: 1,
        meta: {
          fileName: item.fileName,
          fileSize: item.fileSize,
          mimeType: item.mimeType,
          width: item.width,
          height: item.height,
          duration: item.duration,
          file: item.fileKey,
          ...(item.thumbKey ? { thumb: item.thumbKey } : {}),
        },
      },
    },
  }));
}

/** "Sent 4 photos" and friends, as a key and its count (D219). */
export function albumSummary(counts: { photos: number; videos: number }): {
  key: 'photos' | 'videos' | 'mixed';
  n: number;
} {
  const n = counts.photos + counts.videos;
  if (counts.photos === 0) return { key: 'videos', n };
  if (counts.videos === 0) return { key: 'photos', n };
  return { key: 'mixed', n };
}
